import { getElementChildren, getElements } from '../Utility/Dom'
import { IS_ACTIVE } from '../Utility/State'

class NavAnchor {
  private static readonly STUCK_BUFFER = 100

  private links: [] | NodeListOf<HTMLElement>
  private markers: HTMLElement[]
  private readonly nav: HTMLElement
  private offset: number = 0
  private stickyTop: number = 0
  private topBarHeight: number = 0

  constructor(nav: HTMLElement) {
    this.links = getElementChildren(nav, 'nav-anchor-link')
    this.markers = []
    this.nav = nav
  }

  init(): void {
    this.addMarkers()
    this.updateTopBarHeight()
    this.updateOffset()
    this.updateStickyTop()
    this.updateOverflowState()
    this.updateStuckState()
    this.addEvents()
  }

  private addEvents(): void {
    const list = this.nav.querySelector('[data-js=nav-anchor-list]')

    list?.addEventListener('scroll', (): void => this.updateOverflowState(), { passive: true })
    window.addEventListener('scroll', (): void => {
      this.onScroll()
      this.updateStuckState()
    }, { passive: true })

    const resizeObserver = new ResizeObserver((): void => {
      this.updateTopBarHeight()
      this.updateOffset()
      this.updateStickyTop()
      this.updateOverflowState()
    })

    if (list !== null) {
      resizeObserver.observe(list)
    }
  }

  private addMarkers(): void {
    this.links.forEach((link: HTMLElement): void => {
      const url = new URL((link as HTMLAnchorElement).href)

      if (!url.hash) {
        return
      }

      const target = document.querySelector<HTMLElement>(url.hash)

      if (target === null) {
        return
      }

      this.markers.push(target)
    })
  }

  private onScroll(): void {
    let activeIndex: number = -1

    this.markers.forEach((marker: HTMLElement, index: number): void => {
      const markerTop: number = marker.getBoundingClientRect().top

      if (markerTop <= this.offset) {
        activeIndex = index
      }
    })

    this.links.forEach((link: HTMLElement, index: number): void => {
      if (index === activeIndex) {
        link.classList.add(IS_ACTIVE)

        const list = link.parentElement?.parentElement

        if (list) {
          const linkCenter: number = link.offsetLeft + link.offsetWidth / 2
          const listCenter: number = list.clientWidth / 2
          const targetScroll: number = linkCenter - listCenter

          list.scrollTo({
            behavior: 'smooth',
            left: targetScroll,
          })
        }
      } else {
        link.classList.remove(IS_ACTIVE)
      }
    })
  }

  private updateOffset(): void {
    this.offset = this.topBarHeight + this.nav.offsetHeight
  }

  private updateOverflowState(): void {
    const list = this.nav.querySelector('[data-js=nav-anchor-list]')

    if (list === null) {
      return
    }

    const { clientWidth, scrollLeft, scrollWidth } = list
    const scrollEnd = scrollWidth - clientWidth

    this.nav.classList.toggle('nav-anchor--overflow-start', scrollLeft > 0)
    this.nav.classList.toggle('nav-anchor--overflow-end', scrollLeft < scrollEnd - 1)
  }

  private updateStickyTop(): void {
    this.stickyTop = parseFloat(getComputedStyle(this.nav).top) || 0
  }

  private updateStuckState(): void {
    const isStuck = this.nav.getBoundingClientRect().top <= (this.stickyTop + NavAnchor.STUCK_BUFFER)
    document.documentElement.classList.toggle('is-nav-anchor-stuck', isStuck)
  }

  private updateTopBarHeight(): void {
    this.topBarHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--top-bar-height')) || 0
  }
}

const items: NodeListOf<HTMLElement> = getElements('nav-anchor')
items.forEach((item: HTMLElement): void => {
  const navAnchor = new NavAnchor(item)
  navAnchor.init()
})
